// src/resources/customerLifecycle/CustomerLifecyclePage.tsx
//
// Tela "Ciclo de vida da base" (spec docs/superpowers/specs/2026-09-29-recuperacao-clientes-campanha-design.md
// §8.2, plano Task 10). Chega pelo botão "Ver detalhes" do card "Ciclo de vida
// da base" do Painel 360 (LoyaltySection.tsx) com `atRiskDays`/`inactiveDays`
// na URL, ou direto pelo botão "Criar regra de recuperação →" do card "Receita
// em risco" (sem params — usa o padrão da rede).
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDataProvider, useGetOne, useNotify, usePermissions } from 'react-admin';
import {
  Alert, Autocomplete, Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Grid, Paper, Skeleton, TextField, Tooltip, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import Permission from '../../components/Permission';
import RequiredChain from '../../components/RequiredChain';
import { PERMISSIONS } from '../../constants';
import { getChainId } from '../../lib';
import { TagType } from '../../models';
import { LifecycleSegment, LifecycleSummary, fetchLifecycleSummary, saveLifecycleThresholds } from './api';
import LifecycleCustomersTable from './LifecycleCustomersTable';
import LifecycleRangeSlider from './LifecycleRangeSlider';
import { canFetchLifecycle, isLifecycleRangeValid } from './lifecycleRange';

interface Tag { id: string; name: string; color?: string | null; }

const SEGMENT_META: Record<'active' | 'at_risk' | 'inactive', { label: string; color: string }> = {
  active: { label: 'Ativos', color: '#22c55e' },
  at_risk: { label: 'Em risco', color: '#f59e0b' },
  inactive: { label: 'Inativos', color: '#ef4444' },
};

const pct = (part: number, total: number) => (total > 0 ? `${Math.round((part / total) * 100)}%` : '0%');

const SegmentCard: React.FC<{
  segment: 'active' | 'at_risk' | 'inactive';
  count: number;
  total: number;
  hint?: string;
  selected: boolean;
  onClick: () => void;
  /** Recalculando: o card fica no lugar e só os números viram shimmer (sem piscar a tela). */
  loading?: boolean;
}> = ({ segment, count, total, hint, selected, onClick, loading }) => {
  const meta = SEGMENT_META[segment];
  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 2, cursor: 'pointer', borderWidth: selected ? 2 : 1,
        borderColor: selected ? meta.color : 'divider',
        bgcolor: selected ? `${meta.color}0f` : 'background.paper',
        transition: 'all 0.15s',
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: meta.color }}>{meta.label}</Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 800 }}>
        {loading ? <Skeleton animation="wave" width={120} /> : count.toLocaleString('pt-BR')}
      </Typography>
      {/* Uma string só (não `{pct(...)} da base`, que virariam dois text nodes) — o catálogo es
          casa por texto renderizado inteiro com %{argN}, ver ciclo-vida-01.ts. */}
      <Typography variant="body2" color="text.secondary">
        {loading ? <Skeleton animation="wave" width={90} /> : `${pct(count, total)} da base`}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {loading ? <Skeleton animation="wave" width={200} /> : hint}
        </Typography>
      )}
    </Card>
  );
};

const CustomerLifecyclePage: React.FC = () => {
  const chainId = getChainId();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { permissions } = usePermissions();
  const canManageRecovery = !!permissions?.includes(PERMISSIONS.CUSTOMER_RECOVERY_RULES);
  const canEditChain = !!permissions?.includes(PERMISSIONS.CHAIN);

  const { data: chain } = useGetOne('chains', { id: chainId }, { enabled: !!chainId });
  const usesCustomerRecovery = chain?.useCustomerRecovery === true;

  const [atRiskDays, setAtRiskDays] = useState<number | null>(() => {
    const v = searchParams.get('atRiskDays');
    return v ? Number(v) : null;
  });
  const [inactiveDays, setInactiveDays] = useState<number | null>(() => {
    const v = searchParams.get('inactiveDays');
    return v ? Number(v) : null;
  });
  const [tags, setTags] = useState<Tag[]>([]);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [segment, setSegment] = useState<LifecycleSegment>('at_risk');

  const [summary, setSummary] = useState<LifecycleSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!chainId) return;
    dataProvider
      .getList('chains/chainId/tag', {
        pagination: { page: 1, perPage: 999 },
        sort: { field: 'name', order: 'ASC' },
        filter: { type: TagType.client, chainId },
      })
      .then(({ data }: any) => setTags(data))
      .catch(() => {});
  }, [dataProvider, chainId]);

  // Debounce de 500ms (spec §8.2): evita um request por tecla digitada nos campos de dias.
  useEffect(() => {
    // Faixa digitada fora da regra (ex.: Em risco >= Inativo) não vai pro backend: seria 400.
    if (!chainId) return;
    if (!canFetchLifecycle(atRiskDays, inactiveDays)) { setLoadingSummary(false); return; }
    let canceled = false;
    setLoadingSummary(true);
    const timer = setTimeout(() => {
      fetchLifecycleSummary({ chainId, atRiskDays, inactiveDays, tagIds })
        .then((data) => {
          if (canceled) return;
          setSummary(data);
          // Só preenche os campos com o padrão da rede na 1ª carga (sem valor ainda) —
          // depois disso a edição do usuário é que vale.
          setAtRiskDays((prev) => prev ?? data.thresholds.atRiskDays);
          setInactiveDays((prev) => prev ?? data.thresholds.inactiveDays);
        })
        .catch(() => { if (!canceled) notify('Não foi possível calcular o ciclo de vida da base.', { type: 'warning' }); })
        .finally(() => { if (!canceled) setLoadingSummary(false); });
    }, 500);
    return () => { canceled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId, atRiskDays, inactiveDays, JSON.stringify(tagIds)]);

  const rangeValid = isLifecycleRangeValid(atRiskDays, inactiveDays);

  const createRuleAudience = (seg: 'at_risk' | 'inactive') => {
    const audience = seg === 'at_risk'
      ? { minDaysWithoutFueling: atRiskDays ?? 60, maxDaysWithoutFueling: inactiveDays ?? 240, includeNeverFueled: false, tagIds, maxCustomers: null, segment: seg }
      : { minDaysWithoutFueling: inactiveDays ?? 240, maxDaysWithoutFueling: null, includeNeverFueled: false, tagIds, maxCustomers: null, segment: seg };
    navigate(`/customer-recovery-rules/create?audience=${encodeURIComponent(JSON.stringify(audience))}`);
  };

  const handleSaveDefaults = async () => {
    if (!rangeValid || atRiskDays == null || inactiveDays == null) return;
    setSaving(true);
    try {
      await saveLifecycleThresholds(chainId, atRiskDays, inactiveDays);
      notify('Padrão do Painel 360 atualizado.', { type: 'success' });
      setSaveDialogOpen(false);
      setSummary((prev) => (prev ? { ...prev, chainDefaults: { atRiskDays, inactiveDays } } : prev));
    } catch (err: any) {
      notify(err?.body?.message || err?.message || 'Não foi possível salvar o padrão.', { type: 'warning' });
    } finally {
      setSaving(false);
    }
  };

  const total = summary?.total ?? 0;
  // Pré-computado fora do JSX de propósito: `segment as 'active' | ...` dentro de um
  // LogicalExpression filho direto de JSX faz o plugin de build scripts/i18n/babel-plugin-i18n-wrap.mjs
  // (que embrulha texto de tela em __t()) descer no tipo TS e tentar embrulhar o
  // literal do TSLiteralType, quebrando o build ("Property literal of TSLiteralType
  // expected... CallExpression"). Uma referência simples no JSX evita o gatilho.
  const segmentLabel = SEGMENT_META[segment as 'active' | 'at_risk' | 'inactive']?.label ?? segment;

  return (
    <Permission permission={PERMISSIONS.DASHBOARD_V2}>
      <RequiredChain>
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1240, mx: 'auto' }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, mb: 0.5 }}>Ciclo de vida da base</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Quantos clientes estão ativos, em risco de parar ou já inativos — e quem precisa de um empurrão pra voltar.
          </Typography>

          {/* A régua é o controle principal da tela: card próprio no verde da marca, largura toda.
              Os dias exatos se digitam na própria legenda, embaixo da régua. */}
          <Paper
            variant="outlined"
            sx={(theme) => ({
              p: { xs: 2, md: 3 },
              mb: 2,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.07),
              borderColor: alpha(theme.palette.primary.main, 0.35),
            })}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Dias sem abastecer</Typography>
            <Typography variant="body2" color="text.secondary">
              Arraste os pontos ou digite os dias pra definir quando o cliente entra em risco e quando fica inativo.
            </Typography>
            {atRiskDays != null && inactiveDays != null ? (
              <LifecycleRangeSlider
                atRiskDays={atRiskDays}
                inactiveDays={inactiveDays}
                segments={SEGMENT_META}
                onCommit={(risk, inactive) => { setAtRiskDays(risk); setInactiveDays(inactive); }}
              />
            ) : (
              <Skeleton variant="rounded" animation="wave" height={96} sx={{ mt: 1 }} />
            )}
          </Paper>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
            <Autocomplete
              multiple
              size="small"
              options={tags}
              value={tags.filter((t) => tagIds.includes(t.id))}
              onChange={(_, value) => setTagIds(value.map((t) => t.id))}
              getOptionLabel={(t) => t.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderTags={(value, getTagProps) =>
                value.map((t, index) => <Chip label={t.name} size="small" {...getTagProps({ index })} key={t.id} />)}
              renderInput={(params) => <TextField {...params} label="Etiquetas" placeholder="Todas" />}
              sx={{ width: { xs: '100%', sm: 420 } }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<PublishedWithChangesIcon fontSize="small" />}
              disabled={!rangeValid}
              onClick={() => setSaveDialogOpen(true)}
              sx={{ ml: 'auto', flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Usar como padrão do Painel 360
            </Button>
          </Box>

          {!rangeValid && atRiskDays != null && inactiveDays != null && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              "Dias para ficar inativo" precisa ser maior que "Dias para entrar em risco" (e no máximo 3650).
            </Alert>
          )}

          {/* Os cards ficam sempre no lugar (1º carregamento e recálculos): só os números viram
              shimmer, pra tela não piscar a cada mudança dos dias ou das etiquetas. */}
          {(summary || loadingSummary) && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <SegmentCard segment="active" count={summary?.active ?? 0} total={total} loading={loadingSummary} selected={segment === 'active'} onClick={() => setSegment('active')} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <SegmentCard segment="at_risk" count={summary?.atRisk ?? 0} total={total} loading={loadingSummary} selected={segment === 'at_risk'} onClick={() => setSegment('at_risk')} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <SegmentCard
                  segment="inactive"
                  count={summary?.inactive ?? 0}
                  total={total}
                  loading={loadingSummary}
                  hint={summary && summary.neverFueled > 0 ? `inclui ${summary.neverFueled.toLocaleString('pt-BR')} que nunca abasteceram` : undefined}
                  selected={segment === 'inactive'}
                  onClick={() => setSegment('inactive')}
                />
              </Grid>
            </Grid>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Typography sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleAltIcon fontSize="small" color="action" />
              {segmentLabel}
            </Typography>
            {(segment === 'at_risk' || segment === 'inactive') && canManageRecovery && (
              <Tooltip title={usesCustomerRecovery ? '' : 'A recuperação de clientes está desligada nesta rede.'}>
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!usesCustomerRecovery}
                    onClick={() => createRuleAudience(segment as 'at_risk' | 'inactive')}
                  >
                    Criar regra de recuperação
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>

          {(segment === 'at_risk' || segment === 'inactive') && canManageRecovery && chain && !usesCustomerRecovery && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={canEditChain ? (
                <Button color="inherit" size="small" onClick={() => navigate(`/chains/${chainId}`)}>
                  Abrir cadastro da rede
                </Button>
              ) : undefined}
            >
              A recuperação de clientes está desligada nesta rede. Para criar campanhas, ligue "Habilitar recuperação de clientes" no cadastro da rede, aba Configurações.
            </Alert>
          )}

          {chainId && (
            <LifecycleCustomersTable
              chainId={chainId}
              segment={segment}
              atRiskDays={atRiskDays}
              inactiveDays={inactiveDays}
              tagIds={tagIds}
            />
          )}
        </Box>

        <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
          <DialogTitle>Usar como padrão do Painel 360</DialogTitle>
          <DialogContent>
            {/* Uma string só via template literal (não texto+{expr}+texto intercalados) — o
                catálogo es casa o texto inteiro renderizado, ver ciclo-vida-01.ts. */}
            <DialogContentText>
              {`O card "Ciclo de vida da base" do Painel 360 vai passar a usar ${atRiskDays} dias (risco) e ${inactiveDays} dias (inativo) pra toda a rede. Isso não muda os filtros desta tela, só o padrão.`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveDialogOpen(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleSaveDefaults} variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={18} /> : 'Confirmar'}
            </Button>
          </DialogActions>
        </Dialog>
      </RequiredChain>
    </Permission>
  );
};

export default CustomerLifecyclePage;
